/* import __COLOCATED_TEMPLATE__ from './manage-source-dropdown.hbs'; */
/* RESPONSIBLE TEAM: team-knowledge-foundations */
import type AppService from 'embercom/services/app-service';
import moment from 'moment-timezone';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { SyncBehavior } from 'embercom/models/knowledge-hub/sync-source-wrapper';
import { taskFor } from 'ember-concurrency-ts';
import { CAN_MANAGE_KNOWLEDGE_BASE_CONTENT } from 'embercom/lib/knowledge-hub/constants';
import ConnectSourceDropdownOption from './connect-source-dropdown-option';

import type SyncSourceWrapper from 'embercom/models/knowledge-hub/sync-source-wrapper';
import type ArticleSyncSetting from 'embercom/models/articles/article-sync-setting';
import { type InterfaceIconName } from '@intercom/pulse/lib/interface-icons';
import type IntlService from 'ember-intl/services/intl';
import type Store from '@ember-data/store';
import { type SourceRowActionOption, type SourceRowActionsGroupList } from '../sections/section';
import type ContentImportSource from 'embercom/models/content-service/content-import-source';
import { type ButtonType } from '@intercom/pulse/template-registry';
import type KnowledgeHubService from 'embercom/services/knowledge-hub-service';
import type ImportSource from 'embercom/models/import-service/import-source';
import type RouterService from '@ember/routing/router-service';
import type ContentImportLogsModalService from 'embercom/services/content-import-logs-modal-service';
import { type SyncSourceWrapperResponse } from 'embercom/services/knowledge-hub-service';
import type Folder from 'embercom/models/content-service/folder';
import { type EntityType } from 'embercom/models/data/entity-types';
import {
  ActionName,
  actionParameters,
} from '../../filterable-list/bulk-actions/bulk-action-helper';

export interface Signature {
  Args: {
    syncSource?: SyncSourceWrapper;
    type?: ButtonType;
    sectionDisabled?: boolean;
    actionButtonLabel?: string;
    actionOptions?: SourceRowActionsGroupList;
    includeIcon?: boolean;
    includeAddNew?: boolean;
    onAddSyncSource?: (sourceType: string) => void;
    onClickButton?: () => void;
    showBulkEnableOptions?: boolean;
    icon?: InterfaceIconName;
    showIconOnly?: boolean;
    subtle?: boolean;
    hideLabel?: boolean;
    onOpenAdvancedSettings?: (contentImportSource: ContentImportSource) => void;
  };
}

export default class ManageSourceDropdown extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare appService: AppService;
  @service declare permissionsService: $TSFixMe;
  @service declare notificationsService: $TSFixMe;
  @service declare store: Store;
  @service declare router: RouterService;
  @service declare contentImportLogsModalService: ContentImportLogsModalService;
  @service declare modalService: $TSFixMe;

  @tracked sourceToRemove?: SyncSourceWrapper | null;
  @tracked sourceToConvertSyncToImport?: SyncSourceWrapper | null;
  @tracked sourceToEdit?: ContentImportSource | null;
  @tracked sourceEditWrapper?: SyncSourceWrapper | null;
  @tracked showRemoveSourceModal = false;
  @tracked showConvertSyncToImportModal = false;
  @tracked showSourceSettingsModal = false;
  @tracked showBoxModal = false;
  @tracked showDocument360Modal = false;
  @tracked showZendeskModal = false;
  @tracked showConfluenceModal = false;
  @tracked currentView: any;
  @tracked existingImportSource: ImportSource | undefined;
  @service declare knowledgeHubService: KnowledgeHubService;
  @service declare openCenteredWindowService: $TSFixMe;

  @action async requestSourceSettings(syncSource: SyncSourceWrapper) {
    try {
      await this.permissionsService.checkPermission(CAN_MANAGE_KNOWLEDGE_BASE_CONTENT);
    } catch (error) {
      return;
    }
    this.sourceToEdit = await syncSource.show();
    this.sourceEditWrapper = syncSource;
    this.setShowSourceSettingsModal(true);
  }

  get isSectionDisabled(): boolean {
    return this.args.sectionDisabled ?? false;
  }

  get syncSource(): SyncSourceWrapper {
    return this.args.syncSource as SyncSourceWrapper;
  }

  get sourceUsageSummary() {
    if (!this.syncSource) {
      return undefined;
    }

    let sourceUsageSummary = this.knowledgeHubService.usageSummary?.[this.syncSource.entityType];
    if (!sourceUsageSummary) {
      return undefined;
    }
    return sourceUsageSummary.sync_sources.find((source: SyncSourceWrapperResponse) => {
      return source.source_id === this.syncSource.sourceId;
    });
  }

  get canDisableBulkEnableAiAgent(): boolean {
    return this.sourceUsageSummary?.usage?.agent === this.sourceUsageSummary?.usage?.all;
  }

  get canDisableBulkEnableCopilot(): boolean {
    return this.sourceUsageSummary?.usage?.copilot === this.sourceUsageSummary?.usage?.all;
  }

  get actionButtonLabel() {
    if (this.args.hideLabel) {
      return;
    }
    return (
      this.args.actionButtonLabel ??
      this.intl.t('knowledge-hub.overview.sections.syncs.rows.active.action.label')
    );
  }

  get icon(): InterfaceIconName | undefined {
    return this.args.icon ?? (this.args.includeIcon ? 'settings' : undefined);
  }

  @action onClickActionButton() {
    return this.args.onClickButton?.();
  }

  get activeSyncOptions(): SourceRowActionsGroupList {
    if (this.args.actionOptions) {
      return this.args.actionOptions;
    }
    let actions: SourceRowActionOption[] = [];
    let syncSource = this.args.syncSource as SyncSourceWrapper;

    if (this.args.showBulkEnableOptions) {
      actions.push({
        text: this.intl.t(
          'knowledge-hub.overview.sections.syncs.rows.active.action.options.bulk-enable-fin',
        ),
        icon: 'check',
        onSelectItem: async () =>
          await this.applyBulkEnable(
            this.syncSource.entityType,
            ActionName.AIAgentEnable,
            this.syncSource.folderId,
          ),
        isDestructive: false,
        isDisabled: syncSource.isSyncing || this.canDisableBulkEnableAiAgent,
      });
      actions.push({
        text: this.intl.t(
          'knowledge-hub.overview.sections.syncs.rows.active.action.options.bulk-enable-copilot',
        ),
        icon: 'check',
        onSelectItem: async () =>
          await this.applyBulkEnable(
            this.syncSource.entityType,
            ActionName.AICopilotEnable,
            this.syncSource.folderId,
          ),
        isDestructive: false,
        isDisabled: syncSource.isSyncing || this.canDisableBulkEnableCopilot,
      });
    }

    if (syncSource.canResync) {
      actions.push({
        text: this.intl.t(
          'knowledge-hub.overview.sections.syncs.rows.active.action.options.resync',
        ),
        description: this.intl.t('knowledge-hub.overview.sections.syncs.rows.active.last-synced', {
          date: moment(syncSource.lastSyncedAt).fromNow(),
        }),
        icon: 'sync',
        onSelectItem: syncSource.isSyncing ? () => {} : () => taskFor(syncSource.resync).perform(),
        isDestructive: false,
        isDisabled: syncSource.isSyncing || syncSource.syncBehavior === SyncBehavior.API,
        tooltipText: syncSource.isSyncing
          ? this.fetchErrorTooltip('resync', syncSource)
          : undefined,
        tooltip: { isDelayed: false },
      });

      if (syncSource.sourceType === 'external_content') {
        let contentImportSource = syncSource.peekWebsiteSource;
        if (!contentImportSource || contentImportSource?.shouldShowOpenSettingsButton) {
          actions.push({
            text: this.intl.t(
              'knowledge-hub.overview.sections.syncs.rows.active.action.options.open-settings',
            ),
            icon: 'settings',
            onSelectItem: syncSource.isSyncing
              ? () => {}
              : async () => {
                  if (
                    this.args.onOpenAdvancedSettings &&
                    this.appService.app.canUseAudienceAdvancedSyncSettings
                  ) {
                    let source = await syncSource.show();
                    if (source) {
                      this.args.onOpenAdvancedSettings(source);
                    }
                  } else {
                    this.requestSourceSettings(syncSource);
                  }
                },
            isDestructive: false,
            isDisabled: syncSource.isSyncing || syncSource.syncBehavior === SyncBehavior.API,
            tooltipText: syncSource.isSyncing
              ? this.fetchErrorTooltip('settings', syncSource)
              : undefined,
            tooltip: { isDelayed: false },
          });
        }

        if (!this.router.currentRouteName?.includes('knowledge-hub.folder')) {
          actions.push({
            text: this.intl.t('fin-ai-agent.knowledge.actions.websites.view-sync-history'),
            icon: 'history',
            onSelectItem: () =>
              this.contentImportLogsModalService.openContentImportLogsSheet(syncSource),
            isDestructive: false,
            isDisabled: false,
          });
        }

        // Nolaneo – This is extremely useful for test apps. IMO I think we should reinstate this for everyone.
        if (this.appService.app?.canChangeContentImportState) {
          let labelKey = syncSource.isLive
            ? 'knowledge-hub.overview.sections.syncs.rows.active.action.options.disable'
            : 'knowledge-hub.overview.sections.syncs.rows.active.action.options.enable';

          let icon: 'pause' | 'play' = syncSource.isLive ? 'pause' : 'play';

          actions.push({
            text: this.intl.t(labelKey),
            icon,
            onSelectItem: async () => {
              this.notificationsService.notify(
                this.intl.t(
                  'knowledge-hub.overview.sections.syncs.rows.active.action.options.change-status-started',
                ),
              );

              await syncSource.update({
                status: syncSource.isLive ? 'deactivated' : 'active',
              });
            },
            isDestructive: false,
          });
        }
      }
      if (
        syncSource.sourceType === 'box' ||
        syncSource.sourceType === 'document360' ||
        syncSource.sourceType === 'confluence'
      ) {
        actions.push({
          text: this.intl.t(
            'knowledge-hub.overview.sections.syncs.rows.active.action.options.open-settings',
          ),
          icon: 'settings',
          onSelectItem: syncSource.isSyncing
            ? () => {}
            : async () => {
                try {
                  this.reconfigureSyncSource();
                } catch (error) {
                  return;
                }
              },
          isDestructive: false,
          isDisabled: syncSource.isSyncing,
          tooltipText: syncSource.isSyncing
            ? this.fetchErrorTooltip('settings', syncSource)
            : undefined,
          tooltip: { isDelayed: false },
        });
      }
      if (
        syncSource.sourceType === 'box' ||
        syncSource.sourceType === 'document360' ||
        syncSource.sourceType === 'confluence'
      ) {
        actions.push({
          text: this.intl.t(
            'knowledge-hub.overview.sections.syncs.rows.active.action.options.re-authenticate',
          ),
          icon: 'owner',
          onSelectItem: () => this.reauthenticateSource(),
          isDisabled: syncSource.isSyncing,
          tooltipText: syncSource.isSyncing
            ? this.intl.t(
                'knowledge-hub.overview.sections.syncs.rows.active.action.options.disabled-re-authenticate-tooltip',
              )
            : undefined,
          tooltip: { isDelayed: false },
          isDestructive: false,
        });
      }
    }

    if (syncSource.sourceType === 'salesforce_knowledge') {
      actions.push({
        text: this.intl.t(
          'knowledge-hub.overview.sections.syncs.rows.active.action.options.manage-sync',
        ),
        icon: 'settings',
        onSelectItem: () => {
          this.router.transitionTo(this.router.currentRouteName, {
            queryParams: {
              openSalesforceSync: true,
              salesforceSyncStep: 1,
              salesforceSyncSourceId: syncSource.sourceId,
            },
          });
        },
        isDestructive: false,
        isDisabled: syncSource.isSyncing,
      });
    }

    // Add migrate button for old Zendesk sources
    if (syncSource.sourceType === 'zendesk' && this.shouldShowMigrateButton(syncSource)) {
      actions.push({
        text: this.intl.t(
          'knowledge-hub.overview.sections.syncs.rows.active.action.options.migrate',
        ),
        description: this.intl.t(
          'knowledge-hub.overview.sections.syncs.rows.active.action.options.migrate-description',
        ),
        icon: 'upgrade',
        onSelectItem: syncSource.isSyncing ? () => {} : () => this.migrateToNewImporter(syncSource),
        isDestructive: false,
        isDisabled: syncSource.isSyncing,
        tooltipText: syncSource.isSyncing
          ? this.intl.t(
              'knowledge-hub.overview.sections.syncs.rows.active.action.options.disabled-migrate-tooltip',
            )
          : undefined,
        tooltip: { isDelayed: false },
      });
    }

    // Add "Edit source" button for sources with new importer flow
    if (this.canEditSource(syncSource)) {
      actions.push({
        text: this.intl.t(
          'knowledge-hub.overview.sections.syncs.rows.active.action.options.edit-source',
        ),
        icon: 'edit',
        onSelectItem: syncSource.isSyncing ? () => {} : () => this.openEditSourceModal(syncSource),
        isDestructive: false,
        isDisabled: syncSource.isSyncing,
        tooltipText: syncSource.isSyncing
          ? this.intl.t(
              'knowledge-hub.overview.sections.syncs.rows.active.action.options.disabled-edit-tooltip',
            )
          : undefined,
        tooltip: { isDelayed: false },
      });
    }

    if (syncSource.canImport) {
      actions.push({
        text: this.intl.t(
          'knowledge-hub.overview.sections.syncs.rows.active.action.options.import',
        ),
        icon: 'cloud-upload',
        onSelectItem: syncSource.isSyncing
          ? () => {}
          : () => this.requestConvertingSyncToImport(syncSource),
        isDestructive: false,
        isDisabled: syncSource.isSyncing,
        tooltipText: syncSource.isSyncing
          ? this.intl.t(
              'knowledge-hub.overview.sections.syncs.rows.active.action.options.disabled-import-tooltip',
            )
          : undefined,
        tooltip: { isDelayed: false },
      });
    }

    if (syncSource.canRemove) {
      actions.push({
        text: this.intl.t(
          'knowledge-hub.overview.sections.syncs.rows.active.action.options.remove',
        ),
        icon: 'trash',
        onSelectItem: syncSource.isSyncing ? () => {} : () => this.requestRemoveSource(syncSource),
        isDestructive: true,
        isDisabled: syncSource.isSyncing,
        tooltipText: syncSource.isSyncing
          ? this.intl.t(
              'knowledge-hub.overview.sections.syncs.rows.active.action.options.disabled-remove-tooltip',
            )
          : undefined,
        tooltip: { isDelayed: false },
      });
    }

    let options = [{ items: actions }];
    if (this.args.includeAddNew && syncSource.canAddNew) {
      let item: SourceRowActionOption = {
        text: '',
        description: '',
        icon: 'new',
        onSelectItem: () => this.args.onAddSyncSource?.(syncSource.sourceType),
        isDestructive: false,
      };

      if (syncSource.sourceType === 'zendesk') {
        item = {
          ...item,
          text: this.intl.t(
            'knowledge-hub.overview.sections.syncs.rows.active.action.options.new.zendesk.label',
          ),
          description: this.intl.t(
            'knowledge-hub.overview.sections.syncs.rows.active.action.options.new.zendesk.description',
          ),
          icon: 'add',
        };
      } else {
        item = {
          ...item,
          text: this.intl.t(
            'knowledge-hub.overview.sections.syncs.rows.active.action.options.connect',
            {
              type: this.intl.t(
                `knowledge-hub.overview.sections.syncs.types.${syncSource.sourceType}`,
              ),
            },
          ),
          component: ConnectSourceDropdownOption,
        };
      }

      options.push({ items: [item] });
    }
    return options;
  }

  setShowSourceSettingsModal(state = false) {
    this.showSourceSettingsModal = state;
  }

  @action async requestRemoveSource(syncSource: SyncSourceWrapper) {
    try {
      await this.permissionsService.checkPermission(CAN_MANAGE_KNOWLEDGE_BASE_CONTENT);
    } catch (error) {
      return;
    }

    this.sourceToRemove = syncSource;
    this.setShowRemoveSourceModal(true);
  }

  @action async requestConvertingSyncToImport(syncSource: SyncSourceWrapper) {
    try {
      await this.permissionsService.checkPermission(CAN_MANAGE_KNOWLEDGE_BASE_CONTENT);
    } catch (error) {
      return;
    }

    this.sourceToConvertSyncToImport = syncSource;
    this.setShowConvertSyncToImportModal(true);
  }

  @action closeRemoveModal() {
    this.setShowRemoveSourceModal(false);
    this.sourceToRemove = undefined;
  }

  @action async removeSource() {
    let sourceToRemove = this.sourceToRemove;
    if (!sourceToRemove) {
      return;
    }

    await sourceToRemove.remove();
    this.closeRemoveModal();

    if (sourceToRemove.sourceType === 'external_content') {
      // For web syncs, we want to immdiately navigate to the all content page to see the updated state while the deletion is in progress.
      this.router.transitionTo('apps.app.knowledge-hub.all-content');
    }
  }

  @action async convertSyncToImport() {
    let sourceToConvertSyncToImport = this.sourceToConvertSyncToImport;
    if (!sourceToConvertSyncToImport) {
      return;
    }

    await sourceToConvertSyncToImport.import();
    this.closeConvertSyncToImportModal();
  }

  @action closeConvertSyncToImportModal() {
    this.setShowConvertSyncToImportModal(false);
    this.sourceToConvertSyncToImport = undefined;
  }

  @action
  async reconfigureSyncSource() {
    try {
      await this.permissionsService.checkPermission(CAN_MANAGE_KNOWLEDGE_BASE_CONTENT);
    } catch (error) {
      this.notificationsService.notifyError(
        this.intl.t('knowledge-hub.sync-modal.errors.no-permission-source'),
      );
      return;
    }

    try {
      let sourceId = Number(this.args.syncSource?.sourceId);
      if (!sourceId) {
        throw new Error('No source ID provided');
      }

      this.existingImportSource = await this.store.findRecord(
        'import-service/import-source',
        sourceId,
      );

      if (!this.existingImportSource) {
        throw new Error('Import source not found');
      }

      let sourceType = this.args.syncSource?.sourceType;
      if (!sourceType) {
        throw new Error('No source type provided');
      }

      // Show the Box item selector modal or Document360 item selector modal or Confluence modal
      this.currentView = {
        id: sourceType,
        title: this.intl.t(`knowledge-hub.sync-modal.${sourceType}.reconfigure-title`),
        component: `knowledge-hub/setup-modal/sync-modal/${sourceType}`,
        componentArgs: {
          isReconfiguring: true,
          isReauthenticating: true,
          existingImportSource: this.existingImportSource,
        },
      };

      if (sourceType === 'box') {
        this.showBoxModal = true;
      } else if (sourceType === 'document360') {
        this.showDocument360Modal = true;
      } else if (sourceType === 'confluence') {
        this.showConfluenceModal = true;
      } else {
        throw new Error(`Unsupported source type: ${sourceType}`);
      }
    } catch (error) {
      console.error('Error reconfiguring source:', error);
      this.notificationsService.notifyError(
        this.intl.t('knowledge-hub.sync-modal.errors.reconfigure-failed'),
      );
    }
  }

  @action async reauthenticateSource() {
    let sourceType = this.args.syncSource?.sourceType;

    if (sourceType === 'box' || sourceType === 'confluence') {
      await this.reauthenticateOAuthSource(sourceType);
    } else if (sourceType === 'document360') {
      await this.reauthenticateCredentialBasedSource();
    } else {
      this.notificationsService.notifyError(
        this.intl.t('knowledge-hub.sync-modal.re-authenticate.error', {
          source: sourceType,
        }),
      );
    }
  }

  private async reauthenticateOAuthSource(sourceType: 'box' | 'confluence') {
    try {
      await this.permissionsService.checkPermission(CAN_MANAGE_KNOWLEDGE_BASE_CONTENT);
    } catch (error) {
      return;
    }

    let importSource = await this.store.findRecord(
      'import-service/import-source',
      Number(this.args.syncSource?.sourceId),
    );

    if (!importSource) {
      return;
    }
    let url = this.knowledgeHubService.getOAuthURL(importSource.id);

    // eslint-disable-next-line @intercom/intercom/no-bare-strings
    let window = this.openCenteredWindowService.setup(url, 800, 1280, 'Import');

    if (window) {
      window.focus();
      await this.waitForWindowClose(window);
    }

    await importSource.reload();

    if (importSource.hasCredentials) {
      if (this.args.syncSource?.resync) {
        await taskFor(this.args.syncSource.resync).perform();
      }
      this.notificationsService.notifyConfirmation(
        this.intl.t(`knowledge-hub.sync-modal.${sourceType}.re-authenticate-success`),
      );
    } else {
      this.notificationsService.notify(
        this.intl.t(`knowledge-hub.sync-modal.${sourceType}.re-authenticate-error`),
      );
    }
  }

  private async reauthenticateCredentialBasedSource() {
    try {
      await this.permissionsService.checkPermission(CAN_MANAGE_KNOWLEDGE_BASE_CONTENT);
    } catch (error) {
      return;
    }
    this.existingImportSource = await this.store.findRecord(
      'import-service/import-source',
      Number(this.args.syncSource?.sourceId),
    );

    this.currentView = {
      id: 'document360',
      title: this.intl.t('knowledge-hub.sync-modal.document360.title'),
      component: 'knowledge-hub/setup-modal/sync-modal/document360',
      componentArgs: {
        existingImportSource: this.existingImportSource,
        isReauthenticating: true,
      },
    };
    this.showDocument360Modal = true;
  }

  private waitForWindowClose(window: Window): Promise<void> {
    return new Promise((resolve) => {
      let checkInterval = setInterval(() => {
        if (window.closed) {
          clearInterval(checkInterval);
          resolve();
        }
      }, 200);
    });
  }

  @action closeSourceSettingsModal() {
    this.setShowSourceSettingsModal(false);
    this.sourceToEdit = undefined;
  }

  setShowRemoveSourceModal(state = false) {
    this.showRemoveSourceModal = state;
  }

  setShowConvertSyncToImportModal(state = false) {
    this.showConvertSyncToImportModal = state;
  }

  private fetchErrorTooltip(type: 'resync' | 'settings', syncSource: SyncSourceWrapper) {
    if (syncSource.syncBehavior === SyncBehavior.API) {
      return this.intl.t(
        `knowledge-hub.overview.sections.syncs.rows.active.action.options.disabled-${type}-tooltip-api`,
      );
    }
    return this.intl.t(
      `knowledge-hub.overview.sections.syncs.rows.active.action.options.disabled-${type}-tooltip`,
    );
  }

  private canEditSource(syncSource: SyncSourceWrapper): boolean {
    if (!this.appService.app.canUseZendeskMetadataImporterFlow) {
      return false;
    }

    // Extensible: add more source types here in the future
    let editableSourceTypes = ['zendesk'];
    if (!editableSourceTypes.includes(syncSource.sourceType)) {
      return false;
    }

    // Check if this is a new-style source (has import source record)
    let importSource = this.store.peekRecord('import-service/import-source', syncSource.sourceId);
    return importSource != null;
  }

  private shouldShowMigrateButton(syncSource: SyncSourceWrapper): boolean {
    if (!this.appService.app.canUseFeature('team-knowledge-foundations-zendesk-migration-banner')) {
      return false;
    }

    let importSource = this.store.peekRecord('import-service/import-source', syncSource.sourceId);
    return importSource == null;
  }

  @action
  async migrateToNewImporter(syncSource: SyncSourceWrapper) {
    try {
      await this.permissionsService.checkPermission(CAN_MANAGE_KNOWLEDGE_BASE_CONTENT);
    } catch (error) {
      return;
    }

    try {
      let syncSettings = await this.store.findAll('articles/article-sync-setting');
      let syncSetting = syncSettings.find(
        (setting: ArticleSyncSetting) => setting.id === syncSource.sourceId.toString(),
      );

      if (!syncSetting || !syncSetting.siteUrl) {
        throw new Error('Sync setting not found or missing site URL');
      }

      this.currentView = {
        id: 'zendesk',
        title: this.intl.t('knowledge-hub.sync-modal.zendesk.migration-title'),
        component: 'knowledge-hub/setup-modal/sync-modal/zendesk',
        componentArgs: {
          isMigrating: true,
          existingSyncSetting: syncSetting,
        },
      };

      this.showZendeskModal = true;
    } catch (error) {
      this.notificationsService.notifyError(
        this.intl.t('knowledge-hub.sync-modal.zendesk.migration-failed'),
      );
    }
  }

  @action
  async openEditSourceModal(syncSource: SyncSourceWrapper) {
    try {
      await this.permissionsService.checkPermission(CAN_MANAGE_KNOWLEDGE_BASE_CONTENT);
    } catch (e: unknown) {
      // Permission check failed - permissionsService already displayed error modal
      return;
    }

    let importSource = this.store.peekRecord('import-service/import-source', syncSource.sourceId);

    if (!importSource) {
      this.notificationsService.notifyError(
        this.intl.t('knowledge-hub.sync-modal.errors.source-not-found'),
      );
      return;
    }

    this.modalService.openModal(
      'react/knowledge-hub/add-new-external-source/add-new-external-source-sheet',
      {
        open: true,
        appId: this.appService.app.id,
        adminId: this.appService.app.currentAdmin.id.toString(),
        initialSourceId: 'zendesk',
        initialStep: 'metadata',
        importSourceId: syncSource.sourceId.toString(),
        isEditMode: true,
        onClose: () => this.modalService.closeModal(),
      },
    );
  }

  @action async applyBulkEnable(entityType: EntityType, actionName: ActionName, folderId?: number) {
    let { requiredPermission } = actionParameters[actionName];
    try {
      await this.permissionsService.checkPermission(requiredPermission);
    } catch (e) {
      return;
    }

    let folderIds: string[] | undefined;

    if (folderId) {
      folderIds = [folderId.toString()];
      let folder = this.store.peekRecord('content-service/folder', folderId) as Folder;
      if (folder && folder.descendantIds) {
        folderIds = folder.descendantIds.concat(folderId.toString());
      }
    }

    await taskFor(this.knowledgeHubService.requestBulkEnableTask).perform(
      entityType,
      actionName,
      folderIds,
    );
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'KnowledgeHub::Overview::Shared::ManageSourceDropdown': typeof ManageSourceDropdown;
    'knowledge-hub/overview/shared/manage-source-dropdown': typeof ManageSourceDropdown;
  }
}
